import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { 
  LayoutDashboard, 
  Briefcase, 
  Sparkles, 
  TrendingUp,
  Hexagon,
  Shield,
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  CheckCircle,
  AlertTriangle,
  Info,
  Newspaper,
  Layers,
  ShieldAlert,
  CalendarClock,
  FolderLock,
  Wrench
} from 'lucide-react';
import { Button } from './ui/button';
import { FEATURES } from '../config/features';

const allNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/assets', label: 'Assets', icon: Briefcase },
  { path: '/risk', label: 'Risk', icon: ShieldAlert },
  { path: '/timeline', label: 'Timeline', icon: CalendarClock },
  { path: '/vault', label: 'Vault', icon: FolderLock },
  { path: '/intelligence', label: 'Intelligence', icon: Newspaper },
  { path: '/marketplace', label: 'Marketplace', icon: Hexagon, requiresDT: true, tokenisation: true },
  { path: '/insights', label: 'AI Insights', icon: Sparkles },
  { path: '/opportunities', label: 'Tokenise', icon: Layers, tokenisation: true },
  { path: '/settings', label: 'Settings', icon: Settings },
];
const navItems = allNavItems.filter(i => !i.tokenisation || FEATURES.TOKENISATION_ENABLED);

const allAdminNavItems = [
  { path: '/admin', label: 'Command Centre', icon: Shield },
  { path: '/marketplace', label: 'Marketplace', icon: Hexagon, tokenisation: true },
  { path: '/settings', label: 'Settings', icon: Settings },
];
const adminNavItems = allAdminNavItems.filter(i => !i.tokenisation || FEATURES.TOKENISATION_ENABLED);

const partnerNavItems = [
  { path: '/partner', label: 'Dashboard', icon: Shield },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [unreadCount, setUnreadCount] = React.useState(0);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  // Fetch unread count periodically
  React.useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await api.getUnreadCount();
        setUnreadCount(res.data.count);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleBellClick = async () => {
    setNotifOpen(!notifOpen);
    if (!notifOpen) {
      try {
        const res = await api.getNotifications();
        setNotifications(res.data);
      } catch {}
    }
  };

  const handleReadAll = async () => {
    try {
      await api.markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await api.markNotificationRead(notif.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    }
    setNotifOpen(false);
    if (notif.link) navigate(notif.link);
  };

  // Close menu when route changes
  React.useEffect(() => {
    setMobileMenuOpen(false);
    setNotifOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* PWA Safe Area Spacer - adds padding for iOS notch/status bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] h-[env(safe-area-inset-top)]" />
      
      <nav className="fixed left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-lg border-b border-white/10" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-logo">
              <img 
                src="/assagart-logo.png?v=2" 
                alt="Assagart" 
                className="h-10 sm:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {(user?.role === 'admin' ? adminNavItems : user?.role === 'partner' ? partnerNavItems : navItems)
                .filter(item => !item.requiresDT || user?.digital_twin_access === 'active')
                .map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-2 px-3 py-2 text-sm tracking-wide transition-colors rounded ${
                      isActive 
                        ? 'text-gold bg-gold/10' 
                        : 'text-muted-foreground hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="hidden xl:inline">{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2">
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={handleBellClick}
                  className="relative p-2 text-muted-foreground hover:text-gold rounded transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-[#0A0A0A] border border-[#262626] rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto" data-testid="notification-dropdown">
                    <div className="flex items-center justify-between p-3 border-b border-[#1A1A1A]">
                      <p className="text-white text-sm font-medium">Notifications</p>
                      {unreadCount > 0 && (
                        <button onClick={handleReadAll} className="text-xs text-gold hover:text-gold/80">Mark all read</button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <p className="p-4 text-center text-muted-foreground text-sm">No notifications</p>
                    ) : (
                      notifications.slice(0, 10).map(n => (
                        <button
                          key={n.id}
                          onClick={() => handleNotifClick(n)}
                          className={`w-full text-left p-3 border-b border-[#1A1A1A] hover:bg-white/5 transition-colors ${!n.read ? 'bg-white/[0.02]' : ''}`}
                          data-testid={`notification-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            {n.type === 'success' ? <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" /> :
                             n.type === 'warning' ? <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" /> :
                             n.type === 'renewal' ? <CalendarClock size={14} className="text-amber-300 mt-0.5 flex-shrink-0" /> :
                             <Info size={14} className="text-[#627EEA] mt-0.5 flex-shrink-0" />}
                            <div>
                              <p className={`text-sm ${!n.read ? 'text-white font-medium' : 'text-muted-foreground'}`}>{n.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                              <p className="text-xs text-muted-foreground/50 mt-1">{new Date(n.created_at).toLocaleDateString('en-GB')}</p>
                            </div>
                            {!n.read && <div className="w-2 h-2 rounded-full bg-gold flex-shrink-0 mt-1" />}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                data-testid="nav-logout"
                className="text-muted-foreground hover:text-gold"
              >
                <LogOut size={16} />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            style={{ top: 'env(safe-area-inset-top, 0px)' }}
          />
          
          {/* Dropdown Menu - positioned below navbar with safe area offset */}
          <div 
            className="fixed left-0 right-0 z-50 lg:hidden bg-[#0A0A0A] border-b border-white/10 shadow-xl max-h-[calc(100vh-3.5rem-env(safe-area-inset-top))] overflow-y-auto"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}
          >
            <div className="p-2">
              {(user?.role === 'admin' ? adminNavItems : user?.role === 'partner' ? partnerNavItems : navItems)
                .filter(item => !item.requiresDT || user?.digital_twin_access === 'active')
                .map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-between px-4 py-3.5 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-gold/10 text-gold' 
                        : 'text-white hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} className={isActive ? 'text-gold' : 'text-muted-foreground'} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground" />
                  </Link>
                );
              })}
              
              {/* Divider */}
              <div className="my-2 border-t border-white/10" />
              
              {/* Logout */}
              <button 
                onClick={handleLogout}
                className="flex items-center justify-between w-full px-4 py-3.5 rounded-lg text-red-400 hover:bg-red-400/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <LogOut size={20} />
                  <span className="font-medium">Logout</span>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
